import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { getPublicPublication, type PublicSnapshot } from "../lib/publication-api";
import { PublicationSnapshotView } from "./PublicationSnapshotView";
import { Public3DOverlay, type Public3DTarget } from "./Public3DOverlay";
import { enter3dEligible } from "../domain/voxel-lod";
import { TerritorySocialBar } from "./social/TerritorySocialBar";
import { CommentThread } from "./social/CommentThread";

type Status = "loading" | "empty" | "ready" | "error";

/**
 * Public, read-only Territory publication surface (`#p/<identity_id>`).
 *
 * Renders the IMMUTABLE server publication snapshot ONLY — never the live
 * private Draft. When no active publication exists (never published, or
 * withdrawn by a lifecycle event) it shows an unavailable state and exposes NO
 * canvas content. Full map-integrated visual LOD rendering is the separate
 * "Snapshots + Visual LOD" macroblock; this V1 surface proves the publication
 * authority + Draft isolation with a faithful read-only element render.
 */
export function PublicPublicationView({ identityId }: { identityId: string | null }) {
  const [snapshot, setSnapshot] = useState<PublicSnapshot | null>(null);
  const [status, setStatus] = useState<Status>("loading");
  const [entered3d, setEntered3d] = useState(false);
  const reqRef = useRef(0);

  useEffect(() => {
    if (!identityId) { setStatus("empty"); return; }
    const id = ++reqRef.current;
    const controller = new AbortController();
    setStatus("loading");
    setEntered3d(false);
    getPublicPublication(identityId, controller.signal)
      .then((snap) => {
        if (id !== reqRef.current) return;
        if (snap === null) { setSnapshot(null); setStatus("empty"); return; }
        setSnapshot(snap);
        setStatus("ready");
      })
      .catch((e) => {
        if (id !== reqRef.current) return;
        if ((e as { name?: string }).name === "AbortError") return;
        setStatus("error");
      });
    return () => { reqRef.current++; controller.abort(); };
  }, [identityId]);

  if (status === "loading") {
    return <div data-testid="public-pub-loading" className="p-8 text-text-secondary font-mono text-sm">Loading publication…</div>;
  }
  if (status === "error") {
    return <div data-testid="public-pub-error" className="p-8 text-state-danger font-mono text-sm">Publication unavailable.</div>;
  }
  if (status === "empty" || snapshot === null) {
    return (
      <div data-testid="public-pub-empty" className="p-8 text-text-secondary font-mono text-sm">
        This territory has no published content.
      </div>
    );
  }

  // Conditional PUBLIC 3D: only when the authoritative published snapshot
  // carries a VALID non-empty scene_3d. WebGL/Three is NEVER imported until the
  // visitor explicitly clicks ENTER 3D (Public3DOverlay → LazyVoxelScene). No
  // wallet, no Draft — the immutable published scene only.
  const scene = snapshot.scene_3d;
  const has3d = enter3dEligible(scene);
  const voxelCount = scene?.voxel_count ?? scene?.work_arts?.length ?? 0;
  const target: Public3DTarget | null =
    has3d && scene
      ? { identityId: snapshot.identity_id, displayName: snapshot.display_name, workArts: scene.work_arts, voxelCount }
      : null;

  return (
    <div data-testid="public-pub-root" className="relative">
      <PublicationSnapshotView snapshot={snapshot} />
      {has3d && (
        <div className="px-6 pb-6">
          <button
            data-testid={`enter-3d-btn-${snapshot.identity_id}`}
            data-voxel-count={voxelCount}
            onClick={() => setEntered3d(true)}
            className="inline-flex items-center gap-2 rounded-full border border-neon-teal/55 bg-[rgba(6,11,18,0.78)] px-3.5 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-[#c9f7ff] transition-colors hover:border-neon-teal hover:bg-[rgba(10,18,28,0.9)]"
          >
            <span aria-hidden>◈</span> Enter 3D
          </button>
        </div>
      )}
      {/* Macro #30 — Social Layer V1: engagement bar + comment thread. */}
      <div className="mx-auto w-full max-w-3xl px-6 pb-12">
        <TerritorySocialBar identityId={snapshot.identity_id} />
        <CommentThread identityId={snapshot.identity_id} />
      </div>
      {entered3d && target && createPortal(
        <div className="fixed inset-0 z-modal">
          <Public3DOverlay target={target} onExit={() => setEntered3d(false)} />
        </div>,
        document.body,
      )}
    </div>
  );
}
